import { PracticeScopeProvider } from '../../onboarding/practice/practiceScope';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, ChevronRight, Package, LoaderCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  boldText,
  smallTextSize,
  xxLargeTextSize,
  tertiaryText,
  baseTextSize,
  borderPrimary,
  rounded,
  greenTextColor,
  iconBgGreen,
  iconBgPurple,
  iconBgRed,
} from '../../../styling/stylingHelper';
import { StatCard } from '../../ui/StatCard';
import { TabbedSections } from '../../ui/TabbedSections';
import { CardWithHeader } from '../../ui/CardWithHeader';
import { currencyFormatter } from '../../../services/helperService';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useInventoryViewStore as useInventoryStore } from '../../onboarding/practice/inventoryPractice';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessInventoryPage, canExportInventoryCountReview, canAccessCostPriceAutomation, canUseSuperAdminFeatures } from '../../../services/accessManagement/accessManagement';
import { useImpersonationStore } from '../../../stores/appImpersonationStore';
import { useLoadInventoryCountsHistory, useLoadInventorySections, useLoadCostPrice, useLoadSubrecipes } from '../../../utils/dataLoadingUtils';
import { getTableFormattedDate } from '../../../utils/dateUtils';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { CountReviewProduct, CountReviewAssignment, LinkTargetInput } from '../../../services/inventory/inventoryService';
import { CustomIngredient } from '../../../services/customIngredients/customIngredientsService';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import InventoryAllRestaurantsOverview from './InventoryAllRestaurantsOverview';
import InventoryCountDetailModal from './InventoryCountDetailModal';
import EditCountDateModal from './EditCountDateModal';
import ReLinkInventoryProductModal, { relinkTargetFromReviewRow } from './ReLinkInventoryProductModal';
import LinkProductsModal from './LinkProductsModal';
import SetInventoryPriceModal, { setPriceTargetFromReviewRow } from './SetInventoryPriceModal';
import ProductSetupModal from '../costPrice/new/CostPrice/ProductSetupModal';
import CustomIngredientDetailModal from '../costPrice/new/CostPrice/CustomIngredientDetailModal';
import LeakageTab from './LeakageTab';
import ExpectedInventoryTab from './ExpectedInventoryTab';
import { readInventoryTab, withInventoryTab } from './inventoryTab';
import { firstNotSetupProduct } from '../../onboarding/inventoryValues';
import { inventoryViewToRestore, clearInventoryViewRestore, useInventoryPracticeDataset } from '../../onboarding/practice/inventoryPractice';
import { usePracticeDataset } from '../../onboarding/practice/practiceDataset';
import { RankedValueBars, RankedBarRow } from './RankedValueBars';
import { ValueByCategoryCard, CategoryMode } from './ValueByCategoryCard';
import { InventoryAttentionCard, AttentionTab } from './InventoryAttentionCard';
import {
  groupCountsIntoSessions,
  monthKey,
  SessionGroup,
} from './inventoryOverviewUtils';
import { buildCostPriceInventoryKeys, canRelink, frozenStdUnitPrice, loadCustomIngredientForRow, setupMatchForReviewRow } from './inventoryReviewShared';
import { buildCountReviewExport, countReviewExportFilename } from './inventoryCountExport';
import { ComparedAgainstSlot } from './ComparedAgainstSlot';
import InventoryComparisonPicker from './InventoryComparisonPicker';
import { comparisonGroupKey, InventoryComparison, resolveInventoryComparison } from './inventoryComparison';
import InventorySetupReviewDrawer, { InventorySetupReviewMenu } from './InventorySetupReviewDrawer';
import InventoryExcelSetupDrawer from './InventoryExcelSetupDrawer';

function monthLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function shortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

type MainTab = 'review' | 'expected' | 'variance' | 'history';

const InventoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backendUser, accountSettings } = useAuth();
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  const hasAccess = canAccessInventoryPage(backendUser, accountSettings);
  // Export: always available to an impersonating super admin, else the admin-only
  // `inventory_count_export` account feature.
  const isImpersonating = useImpersonationStore((s) => s.target !== null);
  const canViewExpected = isImpersonating && canUseSuperAdminFeatures(backendUser);
  const canExport = (isImpersonating && canUseSuperAdminFeatures(backendUser)) || canExportInventoryCountReview(backendUser, accountSettings);
  const canReviewSetup = canAccessCostPriceAutomation(backendUser, accountSettings)
    || (isImpersonating && canUseSuperAdminFeatures(backendUser));

  useLoadInventorySections();
  useLoadInventoryCountsHistory();
  // Cost prices + subrecipes feed the "Not setup" attention tab (linked products
  // that no cost price analysis references → leakage would always expect 0).
  useLoadCostPrice();
  useLoadSubrecipes();

  const practice = useInventoryPracticeDataset();
  const practiceActive = !!practice;
  const practiceCost = usePracticeDataset(!!practice);
  const realCostPriceList = useCostPriceStore((s) => s.costPriceList);
  const costPriceList = practiceCost?.costPriceList ?? realCostPriceList;
  const costPriceLoadedFor = useCostPriceStore((s) => s.currentRestaurantId);
  const reloadCustomIngredients = useCostPriceStore((s) => s.reloadCustomIngredients);
  const realSubrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const subrecipesById = practiceCost?.subrecipesById ?? realSubrecipesById;
  const subrecipesLoadedFor = useSubrecipesStore((s) => s.currentRestaurantId);

  // Undefined until BOTH stores hold this restaurant's data — hides the tab
  // instead of flashing every linked product as "not setup" while loading.
  const costPriceKeys = useMemo(() => {
    if (!practice && (!restaurantId || costPriceLoadedFor !== restaurantId || subrecipesLoadedFor !== restaurantId)) return undefined;
    return buildCostPriceInventoryKeys(costPriceList, subrecipesById);
  }, [restaurantId, costPriceLoadedFor, subrecipesLoadedFor, costPriceList, subrecipesById, practice]);

  const {
    groups,
    countReview,
    countsHistory,
    isLoadingCounts,
    error: inventoryError,
    loadCountReview,
    loadCountsHistory,
    countsCompleteForRestaurantId,
    loadSessionSummaries,
    deleteSession,
    invalidateCountReview,
  } = useInventoryStore();

  const selection = countReview.selection;
  const data = countReview.data;
  const kpis = data?.kpis;

  // Main tab + card-level UI state.
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab = readInventoryTab(searchParams, canViewExpected && !practiceActive);
  const setMainTab = useCallback((tab: MainTab) => {
    setSearchParams(previous => withInventoryTab(previous, tab));
  }, [setSearchParams]);
  const [categoryMode, setCategoryMode] = useState<CategoryMode>(() => inventoryViewToRestore(restaurantId)?.categoryMode as CategoryMode ?? 'inventory');
  const [attentionTab, setAttentionTab] = useState<AttentionTab>(() => inventoryViewToRestore(restaurantId)?.attentionTab as AttentionTab ?? 'notLinked');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [leakageComparison, setLeakageComparison] = useState<(InventoryComparison & { scope: string }) | null>(
    () => inventoryViewToRestore(restaurantId)?.leakageComparison as (InventoryComparison & { scope: string }) | null ?? null,
  );
  const [groupSelectorOpen, setGroupSelectorOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [setupReviewOpen, setSetupReviewOpen] = useState(false);
  const [excelSetupOpen, setExcelSetupOpen] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  // History (kept from the previous page — refined in a later phase).
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  // Held by id, not by value: editing a count rewrites it in the store, and the open
  // modal must show the corrected numbers rather than the snapshot it was opened with.
  const [viewingCountId, setViewingCountId] = useState<string | null>(null);
  const [deletingSession, setDeletingSession] = useState<SessionGroup | null>(null);
  const [editingDateSession, setEditingDateSession] = useState<SessionGroup | null>(null);
  const [relinkProduct, setRelinkProduct] = useState<CountReviewProduct | null>(null);
  // Frozen at the moment the automation is opened, so a background reload can't shift
  // the rows the plan was built from.
  const [linkTargets, setLinkTargets] = useState<LinkTargetInput[] | null>(null);
  const [setPriceProduct, setSetPriceProduct] = useState<CountReviewProduct | null>(null);
  const [setupProduct, setSetupProduct] = useState<CountReviewProduct | null>(null);
  // The custom ingredient behind an "Edit" click — resolved from the row before the modal
  // opens, so it mounts in edit mode with the real ingredient.
  const [editingCustomIngredient, setEditingCustomIngredient] = useState<CustomIngredient | null>(null);

  // Assignments behind each deduped super-admin "Not linked (uncounted)" row. One row there
  // stands for the same product in every section it is configured in, so both fix paths (the
  // drawer and the bulk automation) have to fan back out over these.
  const configAssignmentsByKey = useMemo(() => {
    const m = new Map<string, CountReviewAssignment[]>();
    for (const row of data?.configNotLinked ?? []) m.set(row.productKey, row.assignments);
    return m;
  }, [data?.configNotLinked]);

  // "Link products" automation: hand over the not-linked rows of the current selection.
  // Rows without a section can't be re-linked (there is nothing to re-point), so they
  // are dropped rather than silently failing on commit.
  const openLinkAutomation = (rows: CountReviewProduct[]) => {
    const targets = rows
        .filter((r) => canRelink(r) && !!r.sectionId)
        .map((r, i) => {
          // The row's own frozen price per std unit (kg/L/pcs) — shown as a hint so a
          // match with a similar price is easy to spot in the review step.
          const stdPrice = frozenStdUnitPrice(r);
          return {
            rowIndex: i,
            sectionId: r.sectionId as string,
            productKey: r.productKey,
            productName: r.productName,
            sectionName: r.sectionName ?? null,
            inventoryCategoryId: r.inventoryCategoryId ?? null,
            inventoryCategoryName: r.inventoryCategoryName ?? null,
            count: r.neverCounted ? null : r.count,
            countUnit: r.countUnit ?? null,
            price: stdPrice?.value ?? null,
            priceUnit: stdPrice?.unit ?? null,
            // Matched + reviewed once, committed once per section (see LinkTargetInput).
            // `sectionId` above is the first assignment, so only the rest go here.
            additionalTargets: (configAssignmentsByKey.get(r.productKey) ?? [])
              .filter((a) => a.sectionId !== r.sectionId)
              .map((a) => ({ sectionId: a.sectionId, productKey: a.productKey, inventoryCategoryId: a.inventoryCategoryId })),
          };
        });
    // Nothing re-linkable → don't open an empty modal.
    if (targets.length > 0) setLinkTargets(targets);
  };

  // Load the default count-review view (all groups / latest) + the count selector's
  // sessions when the restaurant changes.
  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    const sel = useInventoryStore.getState().countReview.selection;
    loadCountReview(restaurantId, sel);
    loadSessionSummaries(restaurantId, { groupId: sel.groupId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);

  // Comparisons require complete sessions, including counts older than the History preview.
  useEffect(() => {
    if (!restaurantId || isAllRestaurants || practiceActive) return;
    void loadCountsHistory(restaurantId, { complete: true });
  }, [restaurantId, isAllRestaurants, practiceActive, loadCountsHistory]);

  useEffect(() => { setSelectorOpen(false); }, [restaurantId, mainTab, selection.groupId]);

  // Single-group restaurants default to that group once the config loads; with
  // multiple groups the "All inventories" default stays.
  const groupDefaultedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!restaurantId || isAllRestaurants || groups.length === 0) return;
    if (groupDefaultedFor.current === restaurantId) return;
    groupDefaultedFor.current = restaurantId;
    const sel = useInventoryStore.getState().countReview.selection;
    if (groups.length === 1 && sel.groupId === null && sel.sessionId === null) {
      loadCountReview(restaurantId, { groupId: groups[0].id, sessionId: null });
      loadSessionSummaries(restaurantId, { groupId: groups[0].id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants, groups]);

  useEffect(() => {
    if (!practiceActive) return;
    const onTab = (e: Event) => {
      const tab = (e as CustomEvent<{ tab?: MainTab }>).detail?.tab;
      if (tab === 'review' || tab === 'variance' || tab === 'history') setMainTab(tab);
    };
    const onCategoryMode = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: CategoryMode }>).detail?.mode;
      if (mode === 'inventory' || mode === 'product') setCategoryMode(mode);
    };
    const onAttentionTab = (e: Event) => {
      const tab = (e as CustomEvent<{ tab?: AttentionTab }>).detail?.tab;
      if (tab === 'notLinked' || tab === 'notSetup' || tab === 'missingPrice' || tab === 'belowMin' || tab === 'needsReview') {
        setMainTab('review');
        setAttentionTab(tab);
        requestAnimationFrame(() => {
          document.getElementById('inventory-attention')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    };
    const onOpenRelink = () => {
      setMainTab('review');
      setAttentionTab('notLinked');
      const row = useInventoryStore.getState().countReview.data?.products.find(p => !p.linked);
      if (row) setRelinkProduct(row);
    };
    const onCloseRelink = () => setRelinkProduct(null);
    const onOpenSetup = () => {
      setMainTab('review');
      setAttentionTab('notSetup');
      const row = firstNotSetupProduct();
      if (row) setSetupProduct(row);
    };
    const onSelectGroup = (e: Event) => {
      const detail = (e as CustomEvent<{ groupId?: string; tab?: MainTab }>).detail;
      const raw = detail?.groupId;
      if (raw === undefined) return;
      const groupId = raw === '' ? null : raw;
      setMainTab(detail?.tab === 'variance' || detail?.tab === 'history' ? detail.tab : 'review');
      const restaurant = useAppUserDefaultStore.getState().restaurant;
      if (!restaurant || isAllRestaurantsSelected(restaurant)) return;
      useInventoryStore.getState().loadCountReview(restaurant.id, { groupId, sessionId: null });
      useInventoryStore.getState().loadSessionSummaries(restaurant.id, { groupId });
    };
    const onOpenSessions = () => setSelectorOpen(true);
    const onResetComparison = () => setLeakageComparison(null);
    const onCloseSessions = () => setSelectorOpen(false);
    const onOpenSection = (e: Event) => {
      const detail = (e as CustomEvent<{ sectionId?: string; groupId?: string }>).detail;
      if (!detail?.sectionId) return;
      const params = new URLSearchParams();
      if (detail.groupId) params.set('groupId', detail.groupId);
      const qs = params.toString();
      navigate(`/inventory/section/${detail.sectionId}${qs ? `?${qs}` : ''}`);
    };
    window.addEventListener('onboarding:inventory-tab', onTab);
    window.addEventListener('onboarding:inventory-category-mode', onCategoryMode);
    window.addEventListener('onboarding:inventory-attention-tab', onAttentionTab);
    window.addEventListener('onboarding:inventory-open-relink', onOpenRelink);
    window.addEventListener('onboarding:inventory-close-relink', onCloseRelink);
    window.addEventListener('onboarding:inventory-open-setup', onOpenSetup);
    window.addEventListener('onboarding:inventory-select-group', onSelectGroup);
    window.addEventListener('onboarding:inventory-open-section', onOpenSection);
    window.addEventListener('onboarding:inventory-open-sessions', onOpenSessions);
    window.addEventListener('onboarding:inventory-leakage-reset', onResetComparison);
    window.addEventListener('onboarding:inventory-close-sessions', onCloseSessions);
    return () => {
      window.removeEventListener('onboarding:inventory-tab', onTab);
      window.removeEventListener('onboarding:inventory-category-mode', onCategoryMode);
      window.removeEventListener('onboarding:inventory-attention-tab', onAttentionTab);
      window.removeEventListener('onboarding:inventory-open-relink', onOpenRelink);
      window.removeEventListener('onboarding:inventory-close-relink', onCloseRelink);
      window.removeEventListener('onboarding:inventory-open-setup', onOpenSetup);
      window.removeEventListener('onboarding:inventory-select-group', onSelectGroup);
      window.removeEventListener('onboarding:inventory-open-section', onOpenSection);
      window.removeEventListener('onboarding:inventory-open-sessions', onOpenSessions);
      window.removeEventListener('onboarding:inventory-leakage-reset', onResetComparison);
      window.removeEventListener('onboarding:inventory-close-sessions', onCloseSessions);
    };
  }, [navigate, practiceActive, setMainTab]);

  useEffect(() => {
    const onSnapshot = (event: Event) => Object.assign((event as CustomEvent).detail, { categoryMode, attentionTab, leakageComparison });
    const onClose = () => { setSelectorOpen(false); setGroupSelectorOpen(false); setRelinkProduct(null); setSetupProduct(null); setViewingCountId(null); setSetPriceProduct(null); setEditingCustomIngredient(null); setLinkTargets(null); };
    const onReset = () => { onClose(); setCategoryMode('inventory'); setAttentionTab('notLinked'); setLeakageComparison(null); };
    const onRestore = (event: Event) => { const snapshot = (event as CustomEvent).detail; setCategoryMode(snapshot.categoryMode ?? 'inventory'); setAttentionTab(snapshot.attentionTab ?? 'notLinked'); setLeakageComparison(snapshot.leakageComparison ?? null); onClose(); };
    window.addEventListener('onboarding:inventory-snapshot-view', onSnapshot);
    window.addEventListener('onboarding:inventory-reset-view', onReset);
    window.addEventListener('onboarding:inventory-restore-view', onRestore);
    window.addEventListener('onboarding:inventory-close-all', onClose);
    return () => { window.removeEventListener('onboarding:inventory-snapshot-view', onSnapshot); window.removeEventListener('onboarding:inventory-reset-view', onReset); window.removeEventListener('onboarding:inventory-restore-view', onRestore); window.removeEventListener('onboarding:inventory-close-all', onClose); };
  }, [categoryMode, attentionTab, leakageComparison]);

  useEffect(() => { if (!practiceActive) clearInventoryViewRestore(); }, [practiceActive]);

  const sessions = useMemo(() => groupCountsIntoSessions(countsHistory), [countsHistory]);

  const viewingCount = useMemo(
    () => countsHistory.find((c) => c.id === viewingCountId) ?? null,
    [countsHistory, viewingCountId]
  );

  const comparisonReady = !!practice || countsCompleteForRestaurantId === restaurantId;
  const comparisonScope = `${restaurantId}|${practice ? 'practice' : 'real'}|${selection.groupId ?? 'all'}|${selection.sessionId ?? 'latest'}`;
  const leakageWindow = resolveInventoryComparison(sessions, selection.groupId,
    leakageComparison?.scope === comparisonScope ? leakageComparison : {
      endSessionKey: selection.sessionId ? `session:${selection.sessionId}` : null,
    });
  // Preserve automatic legacy predecessors as well as explicitly addressable UUID counts.
  const reviewSessions = sessions;
  const reviewWindow = resolveInventoryComparison(reviewSessions, selection.groupId, {
    endSessionKey: selection.sessionId ? `session:${selection.sessionId}` : null,
    baselineSessionKey: selection.baselineSessionId ? `session:${selection.baselineSessionId}` : null,
  }, { review: true });
  const comparisonWindow = mainTab === 'variance' ? leakageWindow : reviewWindow;
  const reviewAll = mainTab !== 'variance' && selection.groupId === null && selection.sessionId === null;
  const comparisonValue: InventoryComparison = {
    endSessionKey: reviewAll ? null : comparisonWindow.end?.key ?? null,
    baselineSessionKey: reviewAll ? null : comparisonWindow.baseline?.key ?? null,
  };

  if (!hasAccess) return <Navigate to="/" />;

  if (isAllRestaurants) {
    return (
      <div>
        <MobilePageHeader title={t('inventory.title')} right={<RestaurantSelector />} />
        <div className="p-4 max-w-7xl mx-auto">
        <div className="hidden lg:flex items-center gap-3 mb-4">
          <Package className="w-8 h-8 text-green-600 dark:text-green-400" />
          <h1 className="text-3xl font-bold text-text dark:text-text-dark">{t('inventory.title')}</h1>
        </div>
        <InventoryAllRestaurantsOverview />
        </div>
      </div>
    );
  }

  if (!restaurantId) return null;

  // ---- Selection handlers -------------------------------------------------

  const selectGroup = (groupId: string | null) => {
    setLeakageComparison(null);
    loadCountReview(restaurantId, { groupId, sessionId: null });
    loadSessionSummaries(restaurantId, { groupId });
  };

  const applyComparison = (value: InventoryComparison) => {
    setSelectorOpen(false);
    if (mainTab === 'variance') {
      setLeakageComparison({ ...value, scope: comparisonScope });
      return;
    }
    const end = reviewSessions.find(session => session.key === value.endSessionKey);
    const baseline = reviewSessions.find(session => session.key === value.baselineSessionKey);
    void loadCountReview(restaurantId, {
      groupId: end?.groupId ?? null,
      sessionId: end?.counts[0]?.sessionId ?? null,
      baselineSessionId: baseline?.counts[0]?.sessionId ?? null,
    });
  };

  // All-inventories caption, as on the Leakage tab: inventories with no earlier count to compare.
  const skippedNames = (data?.groups ?? [])
    .filter((g) => g.groupId && g.changeValue === null)
    .map((g) => g.groupName ?? t('inventory.ungrouped'));

  const jumpToAttention = (tab: AttentionTab) => {
    setMainTab('review');
    setAttentionTab(tab);
    requestAnimationFrame(() => {
      document.getElementById('inventory-attention')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const toggleSessionExpanded = (key: string) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // "Edit" on a custom-ingredient row: resolve the ingredient behind the row, then open the
  // same side modal the Manage ingredients page uses. Nothing opens when it can't be loaded.
  const openCustomIngredientEditor = async (row: CountReviewProduct) => {
    const ingredient = await loadCustomIngredientForRow(row, restaurantId);
    if (ingredient) setEditingCustomIngredient(ingredient);
  };

  // Saved: the row's name / supplier / price come from the review payload, so it has to be
  // refetched, and the cost-price store's copy of the ingredient is now stale.
  const handleCustomIngredientSaved = async () => {
    await invalidateCountReview(restaurantId);
    await reloadCustomIngredients();
  };

  const handleDeleteSession = async () => {
    if (!deletingSession) return;
    const deletedSessionId = deletingSession.counts[0]?.sessionId ?? null;
    try {
      // If the session being viewed is the one deleted, reset to Latest first so the
      // invalidate-triggered refetch lands on the combined view (not the gone session).
      if (deletedSessionId && selection.sessionId === deletedSessionId) {
        await loadCountReview(restaurantId, { groupId: selection.groupId, sessionId: null });
      }
      await deleteSession(deletingSession.counts.map((c) => c.id), restaurantId);
      loadSessionSummaries(restaurantId, { groupId: selection.groupId });
    } catch (err) {
      console.error('Failed to delete inventory session:', err);
    } finally {
      setDeletingSession(null);
    }
  };

  // ---- Derived view data --------------------------------------------------

  const groupOptions = [
    { value: 'all', label: t('inventory.allInventories') },
    ...groups.map((g) => ({ value: g.id, label: g.name })),
  ];
  const groupSelectorLabel =
    groups.find((g) => g.id === selection.groupId)?.name ?? t('inventory.allInventories');

  // Drilled into one group while several exist → title names the group and a back
  // button returns to the "Value by inventory" overview. Single-group restaurants
  // auto-select their only group, so there is nothing to go back to there.
  const isGroupDrilldown = mainTab !== 'expected' && groups.length > 1 && selection.groupId !== null;
  const pageTitle = isGroupDrilldown
    ? t('inventory.groupPageTitle', { title: t('inventory.title'), group: groupSelectorLabel })
    : t('inventory.title');

  const mainTabOptions = [
    { value: 'review', label: t('inventory.tabCountReview') },
    ...(canViewExpected && !practiceActive ? [{ value: 'expected', label: t('inventory.expected.tab') }] : []),
    { value: 'variance', label: t('inventory.tabLeakage') },
    { value: 'history', label: t('inventory.tabHistory') },
  ];

  const isLoadingReview = countReview.isLoading && !data;
  const hasCounts = !!data && data.products.length > 0;

  // Total-value change as a percentage for the StatCard pill.
  const changePct =
    kpis && kpis.previousTotalValue && kpis.changeValue != null && kpis.previousTotalValue !== 0
      ? (kpis.changeValue / kpis.previousTotalValue) * 100
      : undefined;
  // An explicit baseline's date, named on the KPI card and in both exports; null = "vs previous".
  const baselineLabel = data?.selection.baselineCompletedAt ? getTableFormattedDate(new Date(data.selection.baselineCompletedAt)) : null;

  // Value card 1: groups (All view) vs sections (a group / session is selected).
  const showGroups = selection.groupId === null && selection.sessionId === null;

  // Preserve the current viewing selection when drilling into a section detail page.
  const sectionQuery = (): string => {
    const params = new URLSearchParams();
    if (selection.groupId) params.set('groupId', selection.groupId);
    if (selection.sessionId) params.set('sessionId', selection.sessionId);
    if (selection.baselineSessionId) params.set('baselineSessionId', selection.baselineSessionId);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  // Group-level subline shows the below-minimum count; the minimum is a cross-section
  // total so section-level flags would be misleading (sections use the plain count only).
  const belowMinRightPill = (belowMinCount: number): React.ReactNode => {
    if (belowMinCount > 0) {
      return (
        <span className="text-[12px] leading-[18px] font-medium px-1 sm:px-2 py-1 rounded-[5px] whitespace-nowrap bg-[#D92D201A] text-[#D92D20] dark:bg-[#B91C1C40] dark:text-[#F87171]">
          {t('inventory.belowMinimumLabel', { count: belowMinCount })}
        </span>
      );
    }
    return (
      <span className="text-[12px] leading-[18px] font-medium px-1 sm:px-2 py-1 rounded-[5px] whitespace-nowrap bg-[#2E90FA1A] text-[#2E90FA] dark:bg-[#0EA5E91A] dark:text-[#0EA5E9]">
        {t('inventory.belowMinimumLabel', { count: 0 })}
      </span>
    );
  };



  const valueCard1Rows: RankedBarRow[] = showGroups
    ? (data?.groups ?? []).map((g) => ({
      id: g.groupId ?? 'ungrouped',
      onboardingId: practice ? 'inventory-value-drill' : undefined,
      label: g.groupName ?? t('inventory.ungrouped'),
      value: g.totalValue,
      share: g.share,
      changeValue: g.changeValue,
      subline: <span className={cn(tertiaryText)}>{t('inventory.productCountLabel', { count: g.productCount })}</span>,
      rightPill: belowMinRightPill(g.belowMinCount),
      onClick: g.groupId ? () => selectGroup(g.groupId) : undefined,
    }))
    : (data?.sections ?? []).map((s) => ({
      id: s.sectionId ?? `${s.sectionName}-deleted`,
      onboardingId: practice && s.sectionId === practice.sections[0]?.id ? 'inventory-value-section' : undefined,
      label: s.sectionName,
      value: s.totalValue,
      share: s.share,
      changeValue: s.changeValue,
      subline: <span className={cn(tertiaryText)}>{t('inventory.productCountLabel', { count: s.productCount })}</span>,
      onClick: s.sectionId ? () => navigate(`/inventory/section/${s.sectionId}${sectionQuery()}`) : undefined,
    }));

  // ---- History (group-filtered, ignores the count selector) ---------------

  // History follows the group filter tabs: a specific group shows only that group's
  // sessions; legacy no-group sessions (groupId null) appear only under "All".
  const visibleSessions =
    selection.groupId === null ? sessions : sessions.filter((s) => s.groupId === selection.groupId);
  const visibleSessionsByMonth = (() => {
    const monthGroups: { key: string; label: string; sessions: SessionGroup[] }[] = [];
    const byKey = new Map<string, { key: string; label: string; sessions: SessionGroup[] }>();
    for (const s of visibleSessions) {
      const k = monthKey(s.completedAt);
      let g = byKey.get(k);
      if (!g) {
        g = { key: k, label: monthLabel(s.completedAt), sessions: [] };
        byKey.set(k, g);
        monthGroups.push(g);
      }
      g.sessions.push(s);
    }
    return monthGroups;
  })();

  const selectorLabel = reviewAll ? t('inventory.comparison.allLatest') : comparisonWindow.end
    ? `${selection.groupId === null ? `${comparisonWindow.end.groupName ?? t('inventory.ungrouped')} · ` : ''}${comparisonWindow.baseline ? shortDate(comparisonWindow.baseline.completedAt) + ' – ' : ''}${shortDate(comparisonWindow.end.completedAt)}`
    : t('inventory.selectorLatest');

  const exportContext = () => ({
    restaurantName: selectedRestaurant && !isAllRestaurants ? selectedRestaurant.name : '',
    groupLabel: groupSelectorLabel,
    sessionLabel: selectorLabel,
    baselineLabel,
    generatedAt: new Date(),
  });

  // Build the count-review .txt export from the already-loaded payload and download it.
  const handleExportTxt = () => {
    if (!data) return;
    const ctx = exportContext();
    const markdown = buildCountReviewExport(t, data, ctx);
    const blob = new Blob([markdown], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = countReviewExportFilename(ctx.restaurantName, ctx.generatedAt);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Branded .xlsx export of a single group's count. The module (and exceljs) is
  // dynamically imported so it stays out of the main bundle until first use.
  const canExportExcel = selection.groupId !== null;
  const handleExportExcel = async () => {
    if (!data || !canExportExcel || isExportingExcel) return;
    setIsExportingExcel(true);
    try {
      const { exportCountReviewExcel } = await import('./inventoryCountExcelExport');
      await exportCountReviewExcel(t, data, exportContext());
    } catch (err) {
      console.error('Failed to export inventory count to Excel:', err);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <PracticeScopeProvider value={!!practice}>
    <div>
      {practice && <p role="status" className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">{t(practice.source === 'customer' ? 'onboarding.customerPracticeNotice' : 'onboarding.practiceNotice')}</p>}
      <MobilePageHeader title={pageTitle} right={<RestaurantSelector />} />
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className={cn('items-start gap-2', isGroupDrilldown ? 'flex' : 'hidden lg:flex')}>
          {isGroupDrilldown && (
            <button
              onClick={() => selectGroup(null)}
              aria-label={t('inventory.backToAllInventories') as string}
              className={cn('flex items-center gap-1.5 lg:mt-1.5', smallTextSize, semiBoldText, secondaryText)}
            >
              <ArrowLeft className="w-6 h-6 flex-none" />
              <span className="lg:hidden">{t('inventory.backToAllInventories')}</span>
            </button>
          )}
          <div className="hidden lg:block">
            <h1 className={cn(xxLargeTextSize, boldText, primaryText, 'leading-[43px]')}>{pageTitle}</h1>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          {/* Group filter selector (All inventories / one group) */}
          {groups.length > 0 && mainTab !== 'expected' && (
            <div className="relative">
              <button
                data-onboarding="inventory-group-selector"
                onClick={() => setGroupSelectorOpen((o) => !o)}
                className={cn(smallTextSize, semiBoldText, 'px-3 py-[11px] leading-5 rounded-xl border bg-white', borderPrimary, secondaryText, 'dark:border-[#2D4F68] dark:bg-[#14344C] dark:text-[#CBD5E1] flex items-center gap-1.5')}
              >
                <span className="max-w-[180px] truncate">{groupSelectorLabel}</span>
                {groupSelectorOpen ? <svg width="20" height="20" className='rotate-180 transition duration-200' viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                  : <svg width="20" height="20" className='transition duration-200' viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              </button>
              {groupSelectorOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setGroupSelectorOpen(false)} />
                  <div data-onboarding-filter-panel="inventory-group-selector" className={cn('absolute left-0 sm:right-0 mt-1 w-40 z-20 max-h-80 overflow-y-auto bg-card dark:bg-card-dark border shadow-lg rounded-xl', borderPrimary)}>
                    {groupOptions.map((opt) => {
                      const isActive = (selection.groupId ?? 'all') === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setGroupSelectorOpen(false);
                            selectGroup(opt.value === 'all' ? null : opt.value);
                          }}
                          className={cn('w-full text-left px-3 py-2', smallTextSize, isActive ? cn(greenTextColor, semiBoldText) : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {mainTab !== 'expected' && <div className="relative" data-onboarding="inventory-leakage-dates inventory-count-selector">
            <button type="button" data-onboarding="inventory-count-selector inventory-leakage-dates-btn"
              onClick={() => { if (comparisonReady) setSelectorOpen(true); else void loadCountsHistory(restaurantId, { complete: true }); }} disabled={!comparisonReady && isLoadingCounts}
              aria-haspopup="dialog" aria-expanded={selectorOpen}
              className={cn(smallTextSize, semiBoldText, 'flex min-h-[44px] max-w-full items-center gap-2 rounded-xl border px-3 py-2 disabled:opacity-50 bg-white dark:bg-[#14344C]', borderPrimary, secondaryText)}>
              <CalendarClock className="h-4 w-4 shrink-0" />
              <span className="max-w-[300px] truncate">{comparisonReady ? selectorLabel : t(inventoryError && !isLoadingCounts ? 'inventory.comparison.retry' : 'inventory.loading')}</span>
            </button>
          </div>}
          {selectorOpen && comparisonReady && <InventoryComparisonPicker
            sessions={mainTab === 'variance' ? sessions : reviewSessions}
            groupId={selection.groupId}
            value={comparisonValue}
            allowAll={mainTab !== 'variance'}
            onApply={applyComparison}
            onClose={() => setSelectorOpen(false)}
          />}

          <button
            onClick={() => navigate('/inventory/edit')}
            className={cn(smallTextSize, semiBoldText, 'px-3 py-[11px] leading-5 rounded-xl border border-[#0E8965] dark:border-[#0E8965] text-[#0E8965] dark:text-[#0E8965]', 'flex items-center gap-2')}
          >
            <span>{t('inventory.manageSections')}</span>
          </button>
          {canReviewSetup && <InventorySetupReviewMenu onOpen={() => setSetupReviewOpen(true)} onExcelOpen={() => setExcelSetupOpen(true)} />}
        </div>
      </div>

      {/* Main tabs + export */}
      <div className="mb-5 sm:mb-6 flex items-center justify-between gap-2 sm:gap-3">
        <TabbedSections
          onboardingId="inventory-tabs"
          options={mainTabOptions}
          value={mainTab}
          onChange={(v) => setMainTab(v as MainTab)}
          isTimeOptionSelection={true}
        />

        {canExport && mainTab === 'review' && (
          <div className="relative flex-none">
            <button
              onClick={() => setExportMenuOpen((o) => !o)}
              disabled={!data || isExportingExcel}
              className={cn(smallTextSize, semiBoldText, 'px-4 py-[11px] rounded-xl border bg-white', borderPrimary, secondaryText, 'dark:border-[#2D4F68] dark:bg-[#14344C] dark:text-[#CBD5E1] flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed')}
            >
              <span>{isExportingExcel ? t('inventory.loading') : t('inventory.exportButton')}</span>
              {exportMenuOpen ? <svg width="20" height="20" className='rotate-180 transition duration-200' viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
                : <svg width="20" height="20" className='transition duration-200' viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            </button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                <div className={cn('absolute right-0 mt-2 w-64 z-20 bg-card dark:bg-card-dark border shadow-lg', borderPrimary, rounded)}>
                  <button
                    onClick={() => {
                      setExportMenuOpen(false);
                      handleExportExcel();
                    }}
                    disabled={!canExportExcel}
                    className={cn('w-full text-left px-3 py-2', smallTextSize, semiBoldText, primaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed')}
                  >
                    <span className="block">{t('inventory.exportExcelOption')}</span>
                    {!canExportExcel && (
                      <span className={cn('block font-normal', tertiaryText)}>{t('inventory.exportExcelNeedsGroup')}</span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setExportMenuOpen(false);
                      handleExportTxt();
                    }}
                    className={cn('w-full text-left px-3 py-2 rounded-b-xl', smallTextSize, semiBoldText, primaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                  >
                    {t('inventory.exportTxtOption')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {mainTab === 'expected' && canViewExpected ? <ExpectedInventoryTab key={restaurantId} restaurantId={restaurantId} initialGroupId={selection.groupId} /> : mainTab === 'variance' ? (!comparisonReady ? (
        <div className="flex justify-center py-16">{inventoryError && !isLoadingCounts
          ? <p role="alert" className={secondaryText}>{t('inventory.comparison.loadFailed')}</p>
          : <LoaderCircle className={cn('h-8 w-8 animate-spin', primaryText)} aria-label={t('inventory.loading')} />}</div>
      ) : (
        <LeakageTab
          restaurantId={restaurantId}
          selection={{
            groupId: leakageWindow.end ? comparisonGroupKey(leakageWindow.end) : selection.groupId ?? '',
            sessionId: leakageWindow.end?.counts[0]?.sessionId ?? null,
            endSessionKey: leakageWindow.end?.key ?? '',
            baselineSessionKey: leakageWindow.baseline?.key ?? '',
          }}
          onBaselineChange={key => setLeakageComparison({ scope: comparisonScope, endSessionKey: leakageWindow.end?.key ?? null, baselineSessionKey: key })}
          canExport={canExport}
          restaurantName={selectedRestaurant && !isAllRestaurants ? selectedRestaurant.name : ''}
        />
      )) : mainTab === 'review' ? (
        <>
          {selection.groupId === null && (
            <div className="mb-4">
              <ComparedAgainstSlot isAll skippedNames={skippedNames} options={[]} value="" onChange={() => {}} />
            </div>
          )}

          {/* KPI row */}
          <div className="mb-5 sm:mb-6 grid gap-2.5 md:gap-6 grid-cols-2 xl:grid-cols-4">
            <div className="h-full min-w-0" data-onboarding="inventory-kpis inventory-kpi-value">
            <StatCard
              icon={
                <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.6673 17.3359C17.5562 17.3359 16.6118 16.947 15.834 16.1693C15.0562 15.3915 14.6673 14.447 14.6673 13.3359C14.6673 12.2248 15.0562 11.2804 15.834 10.5026C16.6118 9.72483 17.5562 9.33594 18.6673 9.33594C19.7784 9.33594 20.7229 9.72483 21.5007 10.5026C22.2784 11.2804 22.6673 12.2248 22.6673 13.3359C22.6673 14.447 22.2784 15.3915 21.5007 16.1693C20.7229 16.947 19.7784 17.3359 18.6673 17.3359ZM9.33398 21.3359C8.60065 21.3359 7.97287 21.0748 7.45065 20.5526C6.92843 20.0304 6.66732 19.4026 6.66732 18.6693V8.0026C6.66732 7.26927 6.92843 6.64149 7.45065 6.11927C7.97287 5.59705 8.60065 5.33594 9.33398 5.33594H28.0007C28.734 5.33594 29.3618 5.59705 29.884 6.11927C30.4062 6.64149 30.6673 7.26927 30.6673 8.0026V18.6693C30.6673 19.4026 30.4062 20.0304 29.884 20.5526C29.3618 21.0748 28.734 21.3359 28.0007 21.3359H9.33398ZM12.0007 18.6693H25.334C25.334 17.9359 25.5951 17.3082 26.1173 16.7859C26.6395 16.2637 27.2673 16.0026 28.0007 16.0026V10.6693C27.2673 10.6693 26.6395 10.4082 26.1173 9.88594C25.5951 9.36372 25.334 8.73594 25.334 8.0026H12.0007C12.0007 8.73594 11.7395 9.36372 11.2173 9.88594C10.6951 10.4082 10.0673 10.6693 9.33398 10.6693V16.0026C10.0673 16.0026 10.6951 16.2637 11.2173 16.7859C11.7395 17.3082 12.0007 17.9359 12.0007 18.6693ZM25.334 26.6693H4.00065C3.26732 26.6693 2.63954 26.4082 2.11732 25.8859C1.5951 25.3637 1.33398 24.7359 1.33398 24.0026V10.6693C1.33398 10.2915 1.46176 9.97483 1.71732 9.71927C1.97287 9.46372 2.28954 9.33594 2.66732 9.33594C3.0451 9.33594 3.36176 9.46372 3.61732 9.71927C3.87287 9.97483 4.00065 10.2915 4.00065 10.6693V24.0026H25.334C25.7118 24.0026 26.0284 24.1304 26.284 24.3859C26.5395 24.6415 26.6673 24.9582 26.6673 25.3359C26.6673 25.7137 26.5395 26.0304 26.284 26.2859C26.0284 26.5415 25.7118 26.6693 25.334 26.6693Z" fill="#079455" />
                </svg>
              }
              iconBg={iconBgGreen}
              title={t('inventory.kpiTotalCountedValue')}
              mobileLayout="tile"
              value={kpis?.totalValue ?? 0}
              valueType="currency"
              loading={isLoadingReview}
              changeValue={changePct}
              changeLabel={baselineLabel ? t('inventory.vsDate', { date: baselineLabel }) : t('inventory.vsPrevious')}
              trendDirection="up"
            />
            </div>
            <div className="h-full min-w-0" data-onboarding="inventory-kpis inventory-kpi-counted">
            <StatCard
              icon={
                <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <mask id="mask0_210_26321" maskUnits="userSpaceOnUse" x="2" y="2" width="28" height="28">
                    <path d="M29 29V3H3V29H29Z" fill="white" stroke="white" strokeWidth="2" />
                  </mask>
                  <g mask="url(#mask0_210_26321)">
                    <path d="M29.125 17.0938L22.5625 19.2813V29.1797L29.125 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M16 17.0938L22.5625 19.2813V29.1797L16 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M29.125 17.0938L22.5625 19.2812L16 17.0938L22.5625 14.9062L29.125 17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M16 17.0938L9.4375 19.2813V29.1797L16 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.875 17.0938L9.4375 19.2813V29.1797L2.875 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M16 17.0938L9.4375 19.2812L2.875 17.0938L9.4375 14.9062L16 17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22.5625 5.00781L16 7.19531V17.0938L22.5625 14.9063V5.00781Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.4375 5.00781L16 7.19531V17.0938L9.4375 14.9063V5.00781Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22.5625 5.00781L16 7.19531L9.4375 5.00781L16 2.82031L22.5625 5.00781Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                </svg>
              }
              iconBg={iconBgPurple}
              title={t('inventory.kpiProductsCounted')}
              mobileLayout="tile"
              value={kpis?.productsCount ?? 0}
              valueType="number"
              loading={isLoadingReview}
            />
            </div>
            <div className="h-full min-w-0" data-onboarding="inventory-kpis inventory-kpi-unlinked">
            <StatCard
              icon={
                <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <mask id="mask0_210_26348" maskUnits="userSpaceOnUse" x="2" y="2" width="28" height="28">
                    <path d="M29 29V3H3V29H29Z" fill="white" stroke="white" strokeWidth="2" />
                  </mask>
                  <g mask="url(#mask0_210_26348)">
                    <path d="M16.8803 12.9011C17.3304 13.1574 17.7525 13.4794 18.1366 13.8634C20.4439 16.1707 20.507 19.8484 17.6043 22.7511L13.673 26.6824C10.7703 29.5851 7.09261 29.522 4.78535 27.2146C2.47808 24.9074 2.41486 21.2297 5.31756 18.327L8.17969 15.4531" stroke="#2E90FA" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15.1198 19.0991C14.6696 18.8427 14.2476 18.5209 13.8634 18.1367C11.5562 15.8294 11.493 12.1518 14.3957 9.24908L18.327 5.31782C21.2297 2.41506 24.9074 2.47828 27.2147 4.78554C29.522 7.09286 29.5852 10.7705 26.6825 13.6732L23.8203 16.5471" stroke="#2E90FA" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                </svg>
              }
              iconBg="#2E90FA1A"
              title={t('inventory.kpiProductsNotLinked')}
              mobileLayout="tile"
              value={kpis?.notLinkedCount ?? 0}
              valueType="number"
              loading={isLoadingReview}
              onClick={() => jumpToAttention('notLinked')}
              showArrow
            />
            </div>
            <div className="h-full min-w-0" data-onboarding="inventory-kpis inventory-kpi-ordering">
            <StatCard
              icon={
                <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H8.54545L11.5909 19.3029C11.6948 19.829 11.9794 20.3017 12.3949 20.6381C12.8104 20.9744 13.3303 21.1531 13.8636 21.1429H24.9091C25.4424 21.1531 25.9623 20.9744 26.3778 20.6381C26.7933 20.3017 27.0779 19.829 27.1818 19.3029L29 9.71429H9.68182M14.2273 26.8571C14.2273 27.4883 13.7185 28 13.0909 28C12.4633 28 11.9545 27.4883 11.9545 26.8571C11.9545 26.226 12.4633 25.7143 13.0909 25.7143C13.7185 25.7143 14.2273 26.226 14.2273 26.8571ZM26.7273 26.8571C26.7273 27.4883 26.2185 28 25.5909 28C24.9633 28 24.4545 27.4883 24.4545 26.8571C24.4545 26.226 24.9633 25.7143 25.5909 25.7143C26.2185 25.7143 26.7273 26.226 26.7273 26.8571Z" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
              iconBg={iconBgRed}
              title={t('inventory.kpiNeedsOrdering')}
              mobileLayout="tile"
              value={kpis?.needsOrderingCount ?? 0}
              valueType="number"
              loading={isLoadingReview}
              onClick={() => jumpToAttention('belowMin')}
              showArrow
            />
            </div>
          </div>

          {/* Value cards */}
          <div className="mb-5 sm:mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="h-full" data-onboarding="inventory-value">
              {/* Sections view only: the same detail page as a section, over every product of
                  the current selection instead of one section's. Same "see all" link as Order
                  History's "View Products". */}
              <CardWithHeader
                title={showGroups ? t('inventory.valueByInventory') : t('inventory.valueBySection')}
                titleOnboardingId="inventory-value-title"
                seeAllText={showGroups ? undefined : t('inventory.allProductsTitle')}
                onSeeAllClick={() => navigate(`/inventory/all-products${sectionQuery()}`)}
              >
                <RankedValueBars rows={valueCard1Rows} emptyText={hasCounts ? undefined : t('inventory.noCountsEmptyValue')} />
              </CardWithHeader>
            </div>
            <div className="h-full" data-onboarding="inventory-categories">
              <ValueByCategoryCard
                titleOnboardingId="inventory-categories-title"
                modeOnboardingPrefix="inventory-cat-mode"
                categoriesInventory={data?.categoriesInventory ?? []}
                categoriesProduct={data?.categoriesProduct ?? []}
                mode={categoryMode}
                onModeChange={setCategoryMode}
                emptyText={hasCounts ? undefined : t('inventory.noCountsEmptyValue')}
                display="pie"
              />
            </div>
          </div>

          {/* Products needing attention */}
          <div className="mb-5 sm:mb-6" data-onboarding="inventory-attention">
            <InventoryAttentionCard
              id="inventory-attention"
              title={t('inventory.attentionTitle')}
              products={data?.products ?? []}
              extraMissingPriceProducts={data?.configMissingPrice ?? []}
              belowMinProducts={data?.belowMinProducts ?? []}
              needsReviewProducts={data?.needsReview ?? []}
              // Undefined for every non-super-admin (the server omits the field) → no tab.
              configNotLinkedProducts={data?.configNotLinked}
              emptyText={t('inventory.noCountsEmptyValue')}
              activeTab={attentionTab}
              onTabChange={setAttentionTab}
              onRelink={setRelinkProduct}
              onEditCustom={openCustomIngredientEditor}
              onLinkAll={openLinkAutomation}
              onSetPrice={setSetPriceProduct}
              onSetup={setSetupProduct}
              costPriceKeys={costPriceKeys}
            />
          </div>
        </>
      ) : (
        /* History tab (session-grouped) — the delete / count-detail entry point */
        <div>
          <h2 className={cn('text-[20px] sm:text-[24px] leading-7 sm:leading-[32px] font-semibold text-[#161924] dark:text-zinc-50 mb-3')}>{t('inventory.historyTitle')}</h2>
          {isLoadingCounts && countsHistory.length === 0 ? (
            <p className={cn(baseTextSize, tertiaryText, 'text-center py-6')}>{t('inventory.loading')}</p>
          ) : visibleSessions.length === 0 ? (
            <p className={cn(baseTextSize, tertiaryText, 'text-center py-6')}>{t('inventory.noHistory')}</p>
          ) : (
            <div className="space-y-5 sm:space-y-6">
              {visibleSessionsByMonth.map((month) => (
                <div key={month.key}>
                  <p className={cn('text-[16px] leading-6 font-medium text-[#475467] dark:text-[#CBD5E1] uppercase tracking-wide mb-4')}>{month.label}</p>
                  <div className="space-y-4 sm:space-y-5">
                    {month.sessions.map((session) => {
                      const idx = visibleSessions.indexOf(session);
                      const prev = visibleSessions[idx + 1];
                      const delta = prev ? session.totalValue - prev.totalValue : null;
                      const isExpanded = expandedSessions.has(session.key);
                      const sessionId = session.counts[0]?.sessionId ?? null;
                      const isSelected = sessionId != null && selection.sessionId === sessionId;
                      return (
                        <div key={session.key} className={cn('rounded-xl bg-white dark:bg-[#0E2B42] shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]', isSelected ? 'ring-1 ring-textGreen dark:ring-textGreen-dark' : '')}>
                          <div className="py-4 px-3 sm:p-4">
                            {/* Clicking anywhere on the session header expands/collapses it. */}
                            <div
                              className="flex items-start sm:items-center justify-between gap-2 sm:px-2 sm:pt-2 pb-0 cursor-pointer"
                              onClick={() => toggleSessionExpanded(session.key)}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex sm:flex-row flex-col items-start sm:items-center gap-2 mb-3">
                                  <p className={cn('text-[18px] sm:text-[20px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC]')}>
                                    {session.groupName || t('inventory.ungrouped')}
                                  </p>

                                  <div className="flex items-center gap-1">
                                    <span className={cn('text-[12px] sm:text-[14px] leading-[18px] sm:leading-5 font-medium text-[#667085] dark:text-[#CBD5E1]')}>
                                      {t('inventory.sectionCount', { count: session.sectionCount })}
                                    </span>
                                    <div className="w-[3px] h-[3px] rounded-full bg-[#667085] dark:bg-zinc-400" />
                                    <span className="text-[12px] sm:text-[14px] leading-[18px] sm:leading-5 font-medium text-[#667085] dark:text-[#CBD5E1]">{t('inventory.productCountLabel', { count: session.counts.reduce((total, c) => total + (c.items?.length || 0), 0) })}</span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1 text-nowrap">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="7.99967" cy="8.00004" r="6.66667" stroke="currentColor" strokeWidth="1.5" />
                                      <path d="M8 5.33337V8.00004L9.33333 9.33337" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className='text-[#667085] dark:text-[#CBD5E1] font-medium text-[12px] sm:text-[14px] leading-[18px] sm:leading-5'>
                                      {session.completedAt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M12 1.33331V2.66665M4 1.33331V2.66665" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M1.66699 8.16216C1.66699 5.25729 1.66699 3.80486 2.50174 2.90243C3.33648 2 4.67999 2 7.36699 2H8.63366C11.3207 2 12.6642 2 13.4989 2.90243C14.3337 3.80486 14.3337 5.25729 14.3337 8.16216V8.5045C14.3337 11.4094 14.3337 12.8618 13.4989 13.7642C12.6642 14.6667 11.3207 14.6667 8.63366 14.6667H7.36699C4.67999 14.6667 3.33648 14.6667 2.50174 13.7642C1.66699 12.8618 1.66699 11.4094 1.66699 8.5045V8.16216Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M2 5.33331H14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className='text-nowrap text-[#667085] dark:text-[#CBD5E1] font-medium text-[12px] sm:text-[14px] leading-[18px] sm:leading-5'>
                                      {session.completedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-col items-end gap-2">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <div className="text-right">
                                    <p className={cn('sm:mb-3 text-[18px] sm:text-[20px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC]')}>
                                      {currencyFormatter.format(session.totalValue)}
                                    </p>
                                    <div className="hidden sm:flex items-end justify-end">
                                      {delta != null && delta !== 0 && (
                                        <p
                                          className={cn(
                                            'w-fit px-2 text-[14px] rounded-[6px] leading-[26px] font-semibold',
                                            delta >= 0 ? 'text-[#0E8965] dark:text-[#16A34A] bg-[#E7FBF5] dark:bg-[#16A34A1A]' : 'text-[#D92D20] dark:text-[#F87171] bg-[#D92D201A] dark:bg-[#B91C1C40]'
                                          )}
                                        >
                                          {delta >= 0 ? '+' : '-'} {currencyFormatter.format(Math.abs(delta))}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 sm:gap-3">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleSessionExpanded(session.key); }}
                                      title={t(isExpanded ? 'inventory.collapse' : 'inventory.expand') as string}
                                    >
                                      {isExpanded ? (
                                        <svg width="24" height="24" className="rotate-180 transition duration-200" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      ) : (
                                        <svg width="24" height="24" className="transition duration-200" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      )}
                                    </button>
                                    {/* Correct the day a session was counted (finalized late). */}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setEditingDateSession(session); }}
                                      title={t('inventory.editDateAction') as string}
                                      aria-label={t('inventory.editDateAction') as string}
                                    >
                                      <CalendarClock className="w-[22px] h-[22px] text-[#475467] dark:text-[#CBD5E1]" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setDeletingSession(session); }}
                                      title={t('inventory.deleteSession') as string}
                                    >
                                      <svg width="24" height="24" viewBox="0 0 24 24" className="text-[#D92D20] dark:text-[#F87171]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M9.5 16.5L9.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M14.5 16.5L14.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>

                                <div className="sm:hidden flex items-end justify-end">
                                  {delta != null && delta !== 0 && (
                                    <p
                                      className={cn(
                                        'w-fit px-2 text-[14px] rounded-[6px] leading-[26px] font-semibold',
                                        delta >= 0 ? 'text-[#0E8965] dark:text-[#16A34A] bg-[#E7FBF5] dark:bg-[#16A34A1A]' : 'text-[#D92D20] dark:text-[#F87171] bg-[#D92D201A] dark:bg-[#B91C1C40]'
                                      )}
                                    >
                                      {delta >= 0 ? '+' : '-'} {currencyFormatter.format(Math.abs(delta))}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                          </div>

                          {isExpanded && (
                            <div className='px-3 sm:px-4'>
                              <div className="py-2 sm:p-2 border-t border-[#E5E7EB] dark:border-[#1E3A4E]">
                                <ul className="space-y-3">
                                  {session.counts.map((c) => (
                                    // The whole row opens the count's side modal (which carries its
                                    // own edit toggle) — no per-row menu.
                                    <li
                                      key={c.id}
                                      className="flex flex-col gap-2 py-1 sm:py-2 rounded-lg cursor-pointer transition-colors hover:bg-[#F9FAFB] dark:hover:bg-[#13354F] px-2 -mx-2"
                                      onClick={() => setViewingCountId(c.id)}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0 flex flex-col">
                                          <span className={cn('text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50')}>
                                            {c.inventorySectionId === null ? (
                                              <span className="line-through text-[#667085] dark:text-zinc-400">{c.sectionNameSnapshot}</span>
                                            ) : (
                                              c.sectionNameSnapshot
                                            )}
                                          </span>
                                          {c.inventorySectionId === null && (
                                            <span className={cn('ml-2 text-[13px] leading-5 text-[#D92D20] dark:text-textRed-dark')}>{t('inventory.deletedSection')}</span>
                                          )}
                                          <span className={cn('mt-2 text-[14px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1]')}>
                                            {t('inventory.productCountLabel', { count: c.items?.length || 0 })}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-3 flex-none">
                                          <span className={cn('text-[18px] leading-5 font-semibold text-[#101828] dark:text-zinc-50')}>
                                            {currencyFormatter.format(c.totalValue)}
                                          </span>
                                          <ChevronRight className="w-5 h-5 text-[#475467] dark:text-[#CBD5E1]" />
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <EditCountDateModal
        counts={editingDateSession?.counts ?? null}
        restaurantId={restaurantId}
        onClose={() => setEditingDateSession(null)}
      />
      <InventoryCountDetailModal
        isOpen={viewingCount !== null}
        onClose={() => setViewingCountId(null)}
        count={viewingCount}
        restaurantId={restaurantId}
      />
      <DeleteConfirmationModal
        isOpen={deletingSession !== null}
        title={t('inventory.confirmDeleteSession')}
        message={t('inventory.confirmDeleteSessionMessage', {
          date: deletingSession ? shortDate(deletingSession.completedAt) : '',
        })}
        onConfirm={handleDeleteSession}
        onCancel={() => setDeletingSession(null)}
        confirmLabel={t('inventory.delete')}
        cancelLabel={t('inventory.cancel')}
      />
      <ReLinkInventoryProductModal
        product={relinkProduct ? relinkTargetFromReviewRow(relinkProduct, configAssignmentsByKey.get(relinkProduct.productKey)) : null}
        restaurantId={restaurantId}
        onClose={() => setRelinkProduct(null)}
      />
      {linkTargets && (
        <LinkProductsModal
          isOpen
          restaurantId={restaurantId}
          targets={linkTargets}
          onClose={() => setLinkTargets(null)}
          onCommitted={() => loadCountReview(restaurantId, selection)}
        />
      )}
      <SetInventoryPriceModal
        product={setPriceProduct ? setPriceTargetFromReviewRow(setPriceProduct) : null}
        restaurantId={restaurantId}
        onClose={() => setSetPriceProduct(null)}
      />
      {/* Mounted only while open — it lazily loads order + revenue history. Inventory
          rows key their order product by inventoryKey (their productKey); a counted
          subrecipe names the prep itself instead. */}
      {setupProduct && (
        <ProductSetupModal
          practice={!!practice}
          identity={{
            platform: setupProduct.supplier.platform ?? '',
            productName: setupProduct.productName,
            match: setupMatchForReviewRow(setupProduct),
          }}
          restaurantId={restaurantId}
          onClose={() => setSetupProduct(null)}
        />
      )}
      {excelSetupOpen && canReviewSetup && <InventoryExcelSetupDrawer key={restaurantId} restaurantId={restaurantId} onClose={() => setExcelSetupOpen(false)} />}
      {setupReviewOpen && canReviewSetup && (
        <InventorySetupReviewDrawer
          key={restaurantId}
          restaurantId={restaurantId}
          initialGroupId={selection.groupId}
          onClose={() => setSetupReviewOpen(false)}
        />
      )}
      {/* Edit a custom ingredient straight from a review row — same modal as Manage ingredients. */}
      {editingCustomIngredient && (
        <CustomIngredientDetailModal
          ingredient={editingCustomIngredient}
          restaurantId={restaurantId}
          onClose={() => setEditingCustomIngredient(null)}
          onSaved={handleCustomIngredientSaved}
        />
      )}
    </div>
    </PracticeScopeProvider>
  );
};

export default InventoryPage;
